import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  DURATION,
  EASE,
  EASE_PANEL,
  PANEL_DURATION,
  TEXT_STAGGER,
} from "./animation-config.js";

gsap.registerPlugin(ScrollTrigger);

const CONTENT_Y = 48;
const CALLOUT_X = "50%";

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

function measurePanelHeight(panel) {
  panel.style.height = "auto";
  const height = panel.offsetHeight;
  panel.style.height = "0px";

  return height;
}

function setPanelState(panel, isOpen, { animate = true } = {}) {
  if (!panel) {
    return null;
  }

  gsap.killTweensOf(panel);

  if (!animate || reducedMotion) {
    gsap.set(panel, {
      height: isOpen ? "auto" : 0,
      overflow: "hidden",
    });
    return null;
  }

  if (isOpen) {
    const height = measurePanelHeight(panel);

    return gsap.to(panel, {
      height,
      duration: PANEL_DURATION,
      ease: EASE_PANEL,
      overflow: "hidden",
      onComplete: () => {
        panel.style.height = "auto";
      },
    });
  }

  return gsap.to(panel, {
    height: 0,
    duration: PANEL_DURATION,
    ease: EASE_PANEL,
    overflow: "hidden",
  });
}

function setItemState(item, isOpen) {
  const trigger = item.querySelector("[data-jobs-trigger]");

  item.classList.toggle("is-open", isOpen);

  if (trigger) {
    trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
  }
}

function initJobsReveal(section) {
  const content = section.querySelector(".jobs__inner");
  const callout = section.querySelector(".jobs__callout");

  if (!content && !callout) {
    return;
  }

  if (reducedMotion) {
    section.classList.add("is-in-view");
    return;
  }

  if (content) {
    gsap.set(content, { y: CONTENT_Y, opacity: 0 });
  }

  if (callout) {
    gsap.set(callout, { x: CALLOUT_X, opacity: 0 });
  }

  ScrollTrigger.create({
    trigger: section,
    start: "top 70%",
    once: true,
    onEnter: () => {
      section.classList.add("is-in-view");

      const timeline = gsap.timeline({
        defaults: { duration: DURATION, ease: EASE },
      });

      if (content) {
        timeline.to(content, { y: 0, opacity: 1 }, 0);
      }

      if (callout) {
        timeline.to(callout, { x: 0, opacity: 1 }, TEXT_STAGGER);
      }
    },
  });
}

function initJobsAccordion(section) {
  const items = [...section.querySelectorAll("[data-jobs-item]")];

  if (!items.length) {
    return;
  }

  items.forEach((item) => {
    const panel = item.querySelector("[data-jobs-panel]");

    if (!panel) {
      return;
    }

    setPanelState(panel, item.classList.contains("is-open"), {
      animate: false,
    });
  });

  function toggleItem(item) {
    const isOpen = item.classList.contains("is-open");
    const currentItem = items.find((entry) =>
      entry.classList.contains("is-open"),
    );
    const currentPanel = currentItem?.querySelector("[data-jobs-panel]");
    const nextPanel = item.querySelector("[data-jobs-panel]");

    if (isOpen) {
      setItemState(item, false);

      if (reducedMotion) {
        setPanelState(nextPanel, false, { animate: false });
        return;
      }

      setPanelState(nextPanel, false);
      return;
    }

    if (currentItem) {
      setItemState(currentItem, false);
    }

    if (nextPanel) {
      gsap.set(nextPanel, { height: 0, overflow: "hidden" });
    }

    setItemState(item, true);

    if (reducedMotion) {
      setPanelState(currentPanel, false, { animate: false });
      setPanelState(nextPanel, true, { animate: false });
      return;
    }

    const timeline = gsap.timeline({
      defaults: {
        duration: PANEL_DURATION,
        ease: EASE_PANEL,
      },
    });

    if (currentPanel) {
      timeline.add(setPanelState(currentPanel, false), 0);
    }

    if (nextPanel) {
      timeline.add(setPanelState(nextPanel, true), 0);
    }
  }

  items.forEach((item) => {
    const trigger = item.querySelector("[data-jobs-trigger]");

    if (!trigger) {
      return;
    }

    trigger.addEventListener("click", () => {
      toggleItem(item);
    });
  });
}

document.querySelectorAll("[data-jobs]").forEach((section) => {
  initJobsReveal(section);
  initJobsAccordion(section);
});

window.addEventListener(
  "load",
  () => {
    ScrollTrigger.refresh();
  },
  { once: true },
);
